import { Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/**
 * Formata uma data como tempo relativo humano ("agora", "há X min",
 * "há X h", "há X d") pra timestamps recentes, ou como data absoluta
 * curta pra +7 dias. Idioma vem do `TranslateService.currentLang`
 * (PT-BR / EN). Aceita `Date`, string ISO ou null/undefined (retorna '').
 *
 * Uso:
 *   {{ createdAt | relativeTime }}
 *
 * `pure: false` — o valor depende do "agora", então precisamos
 * recalcular a cada CD pra a linha "há 3 min" virar "há 4 min" com o
 * passar do tempo. Mesmo padrão do `datePipe` (também `pure: false`).
 *
 * Consumidores: painel do sino (`user-notifications.component`) e
 * página central (`notifications-center.component`). Antes esse
 * helper vivia duplicado em cada componente.
 */
@Pipe({
  name: 'relativeTime',
  pure: false,
})
export class RelativeTimePipe implements PipeTransform {
  constructor(private translate: TranslateService) {}

  public transform(value: Date | string | null | undefined): string {
    if (!value) { return ''; }
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) { return ''; }
    const diffSec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
    const lang = (this.translate.currentLang || 'pt-BR').startsWith('pt') ? 'pt' : 'en';
    if (diffSec < 60) { return lang === 'pt' ? 'agora' : 'just now'; }
    if (diffSec < 3600) {
      const m = Math.floor(diffSec / 60);
      return lang === 'pt' ? `há ${m} min` : `${m} min ago`;
    }
    if (diffSec < 86400) {
      const h = Math.floor(diffSec / 3600);
      return lang === 'pt' ? `há ${h} h` : `${h} h ago`;
    }
    if (diffSec < 86400 * 7) {
      const dd = Math.floor(diffSec / 86400);
      return lang === 'pt' ? `há ${dd} d` : `${dd} d ago`;
    }
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = d.getFullYear();
    return lang === 'pt' ? `${dd}/${mm}/${yy}` : `${mm}/${dd}/${yy}`;
  }
}
